///
/// EarthBrowser - GeoPackage Data Provider
///
/// SQLite/GeoPackage database data provider
/// Created by Matt Giger on December 17, 2025
/// Copyright (c) 2025 Geodata Labs
///

import type {
	IDataProvider,
	DataRow,
	DataQuery,
	QueryResult,
	ColumnMetadata,
	DataStats,
	ProviderCapabilities,
	FilterGroup,
	FilterCondition,
} from './types'
import { WKBDecoder } from '@/earthbrowser/geopackage/WKBDecoder'

// Minimal structural interface for the SQLite database instance (GeoPackageDatabase)
export interface GeoPackageDatabaseLike {
	exec(sql: string, params?: unknown[]): Promise<unknown>
}

// Row shape returned by PRAGMA table_info
interface TableInfoRow {
	name: string
	type: string
	notnull: number
	pk: number
}

export interface GeoPackageDataProviderConfig {
	id: string
	name: string
	database: GeoPackageDatabaseLike // SQLite database instance (sql.js or similar)
	tableName: string
}

/**
 * GeoPackage/SQLite data provider for database-backed data
 *
 * Features:
 * - SQL-based filtering and sorting
 * - Efficient pagination with LIMIT/OFFSET
 * - Index support
 * - Geometry queries
 * - Transaction support
 */
export class GeoPackageDataProvider implements IDataProvider {
	readonly id: string
	readonly name: string
	readonly type = 'geopackage' as const

	private db: GeoPackageDatabaseLike
	private tableName: string
	private columnCache: ColumnMetadata[] | null = null
	private geometryColumnName: string | null = null

	readonly capabilities: ProviderCapabilities = {
		supportsFiltering: true,
		supportsSorting: true,
		supportsPagination: true,
		supportsFullTextSearch: true, // Basic LIKE-based search implementation
		supportsRegex: true,
		supportsGeometry: true,
		supportsInsert: true,
		supportsUpdate: true,
		supportsDelete: true,
		supportsTransactions: true,
		supportsIndexing: true,
		supportsAggregation: true,
		supportsJoins: true,
		isRemote: false,
		supportsStreaming: false,
		supportsCaching: false,
	}

	constructor(config: GeoPackageDataProviderConfig) {
		this.id = config.id
		this.name = config.name
		this.db = config.database
		this.tableName = config.tableName
	}

	/**
	 * Quote an identifier (table name or column name) for safe use in SQL
	 */
	private quoteIdentifier(identifier: string): string {
		// Escape any existing double quotes by doubling them, then wrap in double quotes
		return `"${identifier.replace(/"/g, '""')}"`
	}

	async getColumns(): Promise<ColumnMetadata[]> {
		if (this.columnCache) {
			return this.columnCache
		}

		// Query SQLite table info
		const results = await this.db.exec(`PRAGMA table_info(${this.quoteIdentifier(this.tableName)})`) as TableInfoRow[]

		if (!results || results.length === 0) {
			return []
		}

		// Detect geometry column (common names: geom, geometry, the_geom, shape, etc.)
		const geometryColumnNames = ['geom', 'geometry', 'the_geom', 'shape', 'wkb_geometry']
		const geometryColumn = results.find(row =>
			geometryColumnNames.includes(row.name.toLowerCase()) ||
			row.type.toLowerCase().includes('blob') ||
			row.type.toLowerCase().includes('geometry')
		)

		if (geometryColumn) {
			this.geometryColumnName = geometryColumn.name
		}

		const columns = results
			.filter(row => row.name !== this.geometryColumnName) // Exclude geometry column
			.map(row => ({
				id: row.name,
				name: row.name,
				type: this.sqliteTypeToColumnType(row.type),
				nullable: row.notnull === 0,
				primaryKey: row.pk === 1,
			}))

		// Add computed geometry_type column if we have a geometry column
		if (this.geometryColumnName) {
			columns.push({
				id: 'geometry_type',
				name: 'geometry_type',
				type: 'string',
				nullable: true,
				primaryKey: false,
			})
		}

		this.columnCache = columns
		return columns
	}

	async getStats(): Promise<DataStats> {
		// Get row count
		const countResult = await this.db.exec(`SELECT COUNT(*) as count FROM ${this.quoteIdentifier(this.tableName)}`) as Array<{ count: number }>
		const totalRows = countResult && countResult.length > 0 ? countResult[0].count : 0

		// Get column count
		const columns = await this.getColumns()

		return {
			totalRows,
			columnCount: columns.length,
		}
	}

	async query(query: DataQuery): Promise<QueryResult> {
		const startTime = performance.now()

		// Build SQL query - include geometry column for type extraction
		const originalGeomCol = this.geometryColumnName
		const { sql, params } = this.buildSQLQuery(query)

		// Execute query - GeoPackageDatabase returns array of objects directly
		const results = await this.db.exec(sql, params) as Record<string, unknown>[]

		// Process rows to decode geometry and add geometry_type if we have a geometry column
		const rows: DataRow[] = (results || []).map(row => {
			if (originalGeomCol && row[originalGeomCol]) {
				try {
					// Decode WKB to GeoJSON geometry for rendering
					const geomBlob = row[originalGeomCol]
					const data = geomBlob instanceof Uint8Array ? geomBlob :
								 geomBlob instanceof ArrayBuffer ? new Uint8Array(geomBlob) :
								 new Uint8Array(geomBlob as ArrayBufferLike)
					const geometry = WKBDecoder.decode(data)
					row.geometry = geometry
					row.geometry_type = geometry.type
				} catch {
					// Fall back to just extracting type if decode fails
					row.geometry_type = this.extractGeometryType(row[originalGeomCol])
				}
				// Remove the raw geometry blob from the result
				delete row[originalGeomCol]
			}
			return row as DataRow
		})

		// Get total count (without pagination) - pass the full query to include search
		const totalCount = await this.count(query.filters, query.search)

		const offset = query.offset || 0
		const limit = query.limit || totalCount

		const executionTime = performance.now() - startTime

		return {
			rows,
			totalCount,
			offset,
			limit,
			hasMore: offset + limit < totalCount,
			executionTime,
			fromCache: false,
		}
	}

	/**
	 * Extract geometry type from WKB blob
	 */
	private extractGeometryType(blob: unknown): string {
		try {
			// Convert to Uint8Array if needed
			const data = blob instanceof Uint8Array ? blob :
						 blob instanceof ArrayBuffer ? new Uint8Array(blob) :
						 new Uint8Array(blob as ArrayBufferLike)

			if (data.length < 5) return 'Unknown'

			let offset = 0

			// Check for GeoPackage Binary header (magic bytes: 'GP' = 0x47 0x50)
			if (data[0] === 0x47 && data[1] === 0x50) {
				// Skip GeoPackage Binary header
				offset += 2 // Magic bytes
				offset += 1 // Version
				const flags = data[offset]
				offset += 1 // Flags
				offset += 4 // SRS ID

				// Skip envelope based on flags bits 1-3
				const envelopeType = (flags >> 1) & 0x07
				switch (envelopeType) {
					case 0: break // No envelope
					case 1: offset += 32; break // XY
					case 2: offset += 48; break // XYZ
					case 3: offset += 48; break // XYM
					case 4: offset += 64; break // XYZM
				}
			}

			// Now at WKB portion
			if (offset + 5 > data.length) return 'Unknown'

			// Skip byte order
			offset += 1

			// Read geometry type (4 bytes, little-endian for most cases)
			const typeCode = data[offset] | (data[offset + 1] << 8) | (data[offset + 2] << 16) | (data[offset + 3] << 24)
			const baseType = typeCode % 1000

			const typeNames: Record<number, string> = {
				1: 'Point',
				2: 'LineString',
				3: 'Polygon',
				4: 'MultiPoint',
				5: 'MultiLineString',
				6: 'MultiPolygon',
				7: 'GeometryCollection'
			}

			return typeNames[baseType] || 'Unknown'
		} catch (error) {
			console.warn('Failed to extract geometry type:', error)
			return 'Unknown'
		}
	}

	async count(filters?: FilterGroup, search?: { query: string; columns?: string[] }): Promise<number> {
		let sql = `SELECT COUNT(*) as count FROM ${this.quoteIdentifier(this.tableName)}`
		const params: unknown[] = []

		// WHERE clause from filters
		if (filters) {
			const whereClause = this.buildWhereClause(filters, params)
			if (whereClause) {
				sql += ` WHERE ${whereClause}`
			}
		}

		// Add search clause (same logic as buildSQLQuery)
		if (search?.query) {
			let columnsToSearch = search.columns || []

			if (columnsToSearch.length === 0 || columnsToSearch.includes('*')) {
				// Get all columns (use cached if available)
				const allColumns = this.columnCache || []
				// Search only text-based columns (exclude geometry, numeric, computed columns, and other non-text types)
				columnsToSearch = allColumns
					.filter(col =>
						col.name !== 'geometry_type' && // Exclude computed columns
						(col.type === 'string' || col.type === 'unknown') // Include unknown types as they might be text
					)
					.map(col => col.name)
			} else {
				// Filter out computed columns from explicit search list
				columnsToSearch = columnsToSearch.filter(col => col !== 'geometry_type')
			}

			if (columnsToSearch.length > 0) {
				const searchConditions = columnsToSearch.map(col =>
					`${this.quoteIdentifier(col)} LIKE ?`
				)
				const searchClause = searchConditions.join(' OR ')

				sql += filters ? ` AND (${searchClause})` : ` WHERE (${searchClause})`

				columnsToSearch.forEach(() => {
					params.push(`%${search.query}%`)
				})
			}
		}

		const results = await this.db.exec(sql, params) as Array<{ count: number }>
		return results && results.length > 0 ? results[0].count : 0
	}

	async insert(rows: DataRow[]): Promise<void> {
		if (rows.length === 0) return

		const columns = Object.keys(rows[0])
		const placeholders = columns.map(() => '?').join(', ')
		const quotedColumns = columns.map(col => this.quoteIdentifier(col)).join(', ')
		const sql = `INSERT INTO ${this.quoteIdentifier(this.tableName)} (${quotedColumns}) VALUES (${placeholders})`

		for (const row of rows) {
			const values = columns.map(col => row[col])
			await this.db.exec(sql, values)
		}
	}

	async update(filters: FilterGroup, updates: Partial<DataRow>): Promise<number> {
		const updateColumns = Object.keys(updates)
		if (updateColumns.length === 0) return 0

		const setClause = updateColumns.map(col => `${this.quoteIdentifier(col)} = ?`).join(', ')
		const params: unknown[] = updateColumns.map(col => updates[col])

		let sql = `UPDATE ${this.quoteIdentifier(this.tableName)} SET ${setClause}`

		const whereClause = this.buildWhereClause(filters, params)
		if (whereClause) {
			sql += ` WHERE ${whereClause}`
		}

		await this.db.exec(sql, params)

		// Get affected rows count
		const changes = await this.db.exec('SELECT changes()') as Array<{ 'changes()': number }>
		return changes && changes.length > 0 ? changes[0]['changes()'] : 0
	}

	async delete(filters: FilterGroup): Promise<number> {
		const params: unknown[] = []
		let sql = `DELETE FROM ${this.quoteIdentifier(this.tableName)}`

		const whereClause = this.buildWhereClause(filters, params)
		if (whereClause) {
			sql += ` WHERE ${whereClause}`
		}

		await this.db.exec(sql, params)

		// Get affected rows count
		const changes = await this.db.exec('SELECT changes()') as Array<{ 'changes()': number }>
		return changes && changes.length > 0 ? changes[0]['changes()'] : 0
	}

	// Private helper methods

	private buildSQLQuery(query: DataQuery): { sql: string; params: unknown[] } {
		const params: unknown[] = []

		// SELECT clause - include geometry column for type extraction in JavaScript
		let selectClause: string
		if (query.columns && query.columns.length > 0) {
			// User specified columns
			const cols = query.columns
				.filter(col => col !== 'geometry_type') // Filter out computed column
				.map(col => this.quoteIdentifier(col))

			// Include geometry column if geometry_type is requested
			if (query.columns.includes('geometry_type') && this.geometryColumnName) {
				cols.push(this.quoteIdentifier(this.geometryColumnName))
			}

			selectClause = cols.join(', ')
		} else {
			// Select all columns including geometry (we'll process and remove it in JavaScript)
			selectClause = '*'
		}

		let sql = `SELECT ${selectClause} FROM ${this.quoteIdentifier(this.tableName)}`

		// WHERE clause
		if (query.filters) {
			const whereClause = this.buildWhereClause(query.filters, params)
			if (whereClause) {
				sql += ` WHERE ${whereClause}`
			}
		}

		// Full-text search (basic implementation using LIKE)
		if (query.search?.query) {
			// If no specific columns are provided or '*' is used, search all text/string columns
			let columnsToSearch = query.search.columns || []

			if (columnsToSearch.length === 0 || columnsToSearch.includes('*')) {
				// Get all columns (use cached if available)
				const allColumns = this.columnCache || []
				// Search only text-based columns (exclude geometry, numeric, computed columns, and other non-text types)
				columnsToSearch = allColumns
					.filter(col =>
						col.name !== 'geometry_type' && // Exclude computed columns
						(col.type === 'string' || col.type === 'unknown') // Include unknown types as they might be text
					)
					.map(col => col.name)
			} else {
				// Filter out computed columns from explicit search list
				columnsToSearch = columnsToSearch.filter(col => col !== 'geometry_type')
			}

			if (columnsToSearch.length > 0) {
				const searchConditions = columnsToSearch.map(col =>
					`${this.quoteIdentifier(col)} LIKE ?`
				)
			const searchClause = searchConditions.join(' OR ')

			sql += query.filters ? ` AND (${searchClause})` : ` WHERE (${searchClause})`

				columnsToSearch.forEach(() => {
				params.push(`%${query.search!.query}%`)
			})
			}
		}

		// ORDER BY clause
		if (query.sort && query.sort.length > 0) {
			const orderBy = query.sort.map(s =>
				`${this.quoteIdentifier(s.column)} ${s.direction.toUpperCase()}`
			).join(', ')
			sql += ` ORDER BY ${orderBy}`
		}

		// LIMIT/OFFSET
		if (query.limit !== undefined) {
			sql += ` LIMIT ${query.limit}`
		}
		if (query.offset !== undefined) {
			sql += ` OFFSET ${query.offset}`
		}

		return { sql, params }
	}

	private buildWhereClause(filter: FilterGroup, params: unknown[]): string {
		const conditions = filter.conditions.map(condition => {
			if ('operator' in condition && (condition.operator === 'AND' || condition.operator === 'OR')) {
				// Nested filter group
				const nested = this.buildWhereClause(condition, params)
				return nested ? `(${nested})` : ''
			} else {
				// Filter condition
				return this.buildConditionSQL(condition as FilterCondition, params)
			}
		}).filter(c => c !== '')

		if (conditions.length === 0) return ''

		return conditions.join(` ${filter.operator} `)
	}

	// Explicitly convert a filter condition value to text for LIKE patterns
	private conditionText(value: unknown): string {
		if (typeof value === 'string') return value
		if (value === null || value === undefined) return String(value)
		if (typeof value === 'number' || typeof value === 'boolean') return String(value)
		if (value instanceof Date) return value.toString()
		if (Array.isArray(value)) return value.map(v => this.conditionText(v)).join(',')
		return JSON.stringify(value)
	}

	private buildConditionSQL(condition: FilterCondition, params: unknown[]): string {
		const col = this.quoteIdentifier(condition.column)

		switch (condition.operator) {
			case 'equals':
				params.push(condition.value)
				return `${col} = ?`

			case 'not_equals':
				params.push(condition.value)
				return `${col} != ?`

			case 'contains':
				params.push(`%${this.conditionText(condition.value)}%`)
				return condition.caseSensitive
					? `${col} LIKE ?`
					: `LOWER(${col}) LIKE LOWER(?)`

			case 'not_contains':
				params.push(`%${this.conditionText(condition.value)}%`)
				return condition.caseSensitive
					? `${col} NOT LIKE ?`
					: `LOWER(${col}) NOT LIKE LOWER(?)`

			case 'starts_with':
				params.push(`${this.conditionText(condition.value)}%`)
				return condition.caseSensitive
					? `${col} LIKE ?`
					: `LOWER(${col}) LIKE LOWER(?)`

			case 'ends_with':
				params.push(`%${this.conditionText(condition.value)}`)
				return condition.caseSensitive
					? `${col} LIKE ?`
					: `LOWER(${col}) LIKE LOWER(?)`

			case 'greater_than':
				params.push(condition.value)
				return `${col} > ?`

			case 'greater_than_or_equal':
				params.push(condition.value)
				return `${col} >= ?`

			case 'less_than':
				params.push(condition.value)
				return `${col} < ?`

			case 'less_than_or_equal':
				params.push(condition.value)
				return `${col} <= ?`

			case 'in':
				if (Array.isArray(condition.value)) {
					const placeholders = condition.value.map(() => '?').join(', ')
					params.push(...condition.value)
					return `${col} IN (${placeholders})`
				}
				return '1=0'

			case 'not_in':
				if (Array.isArray(condition.value)) {
					const placeholders = condition.value.map(() => '?').join(', ')
					params.push(...condition.value)
					return `${col} NOT IN (${placeholders})`
				}
				return '1=1'

			case 'is_null':
				return `${col} IS NULL`

			case 'is_not_null':
				return `${col} IS NOT NULL`

			case 'between':
				if (Array.isArray(condition.value) && condition.value.length === 2) {
					params.push(condition.value[0], condition.value[1])
					return `${col} BETWEEN ? AND ?`
				}
				return '1=0'

			case 'regex':
				params.push(condition.value)
				return `${col} REGEXP ?`

			default:
				return '1=1'
		}
	}

	private sqliteTypeToColumnType(sqliteType: string): ColumnMetadata['type'] {
		const type = sqliteType.toUpperCase()

		if (type.includes('INT')) return 'number'
		if (type.includes('REAL') || type.includes('FLOAT') || type.includes('DOUBLE')) return 'number'
		if (type.includes('TEXT') || type.includes('CHAR') || type.includes('CLOB')) return 'string'
		if (type.includes('BLOB')) return 'unknown'
		if (type.includes('BOOL')) return 'boolean'
		if (type.includes('DATE') || type.includes('TIME')) return 'date'
		if (type.includes('GEOMETRY') || type.includes('POINT') || type.includes('LINE') || type.includes('POLYGON')) return 'geometry'

		return 'unknown'
	}
}

